import { useState } from "react";
import { api } from "@/lib/api";
import NearbyGarages from "@/components/NearbyGarages";
import { Sparkles, Loader2, Check, MapPin, Layers, AlertTriangle, XCircle, CheckCircle2, RefreshCw } from "lucide-react";
import { BACKEND_URL } from "@/lib/api";

const SEVERITY_STYLES = {
  "Minor":      { bg: "#E6F9F0", fg: "#00A86B", icon: CheckCircle2 },
  "Moderate":   { bg: "#FFF6E0", fg: "#A05A00", icon: AlertTriangle },
  "Severe":     { bg: "#FFEDED", fg: "#C8102E", icon: AlertTriangle },
  "Total Loss": { bg: "#1A1A1A", fg: "#FFFFFF", icon: XCircle },
};

function getCsrfCookie() {
  if (typeof document === "undefined") return "";
  const m = document.cookie.match(/(?:^|; )csrf_token=([^;]+)/);
  return m ? decodeURIComponent(m[1]) : "";
}

/**
 * Multi-photo damage estimator: user selects 2-6 accident photos and gets ONE combined assessment.
 * Use this when a claim has multiple accident photos for the same vehicle.
 */
export default function MultiPhotoDamageEstimator({ photos, pincode, insurer, claimId, customerMobile, customerName }) {
  // photos = [{doc_id, original_filename, content_type}, ...]
  const [selected, setSelected] = useState(new Set());
  const [estimate, setEstimate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showGarages, setShowGarages] = useState(false);

  const candidates = (photos || []).filter(
    (d) => (d.content_type || "").startsWith("image/")
  );

  const toggle = (docId) => {
    const next = new Set(selected);
    if (next.has(docId)) next.delete(docId);
    else if (next.size < 6) next.add(docId);
    setSelected(next);
  };

  const run = async () => {
    if (selected.size < 2) {
      setError("Select at least 2 photos");
      return;
    }
    setError("");
    setLoading(true);
    setEstimate(null);
    try {
      const { data } = await api.post("/ai/vision/multi-damage-estimate", {
        doc_ids: Array.from(selected),
      });
      setEstimate(data);
    } catch (e) {
      setError(e.response?.data?.detail || "Multi-photo analysis failed");
    } finally {
      setLoading(false);
    }
  };

  if (candidates.length < 2) return null;

  const sev = estimate ? (SEVERITY_STYLES[estimate.severity] || SEVERITY_STYLES["Minor"]) : null;
  const SevIcon = sev?.icon;

  return (
    <div className="rounded-2xl border border-[#003DA5]/20 bg-gradient-to-br from-[#E6F0FF]/40 to-white p-5" data-testid="multi-photo-estimator">
      <div className="flex items-center gap-2 mb-3">
        <Layers className="w-5 h-5 text-[#003DA5]" />
        <div>
          <div className="font-heading font-bold text-slate-900">Multi-angle AI Assessment</div>
          <div className="text-xs text-slate-600">Select 2-6 photos from different angles for a richer, higher-confidence estimate.</div>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 mb-3" data-testid="multi-photo-grid">
        {candidates.map((d) => {
          const isSelected = selected.has(d.doc_id);
          const imgUrl = `${BACKEND_URL}/api/documents/${d.doc_id}/download`;
          return (
            <button
              type="button"
              key={d.doc_id}
              onClick={() => toggle(d.doc_id)}
              className={`relative aspect-square rounded-lg overflow-hidden border-2 transition-all group ${
                isSelected ? "border-[#003DA5] ring-2 ring-[#003DA5]/30 scale-[0.97]" : "border-slate-200 hover:border-slate-400"
              }`}
              data-testid={`multi-photo-tile-${d.doc_id}`}
            >
              <img src={imgUrl} alt={d.original_filename} className="w-full h-full object-cover" referrerPolicy="no-referrer" />
              {isSelected && (
                <div className="absolute inset-0 bg-[#003DA5]/30 flex items-center justify-center">
                  <div className="w-7 h-7 rounded-full bg-[#003DA5] text-white flex items-center justify-center">
                    <Check className="w-4 h-4" />
                  </div>
                </div>
              )}
              <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/70 to-transparent px-2 py-1 text-[10px] text-white truncate text-left">
                {d.original_filename}
              </div>
            </button>
          );
        })}
      </div>

      <div className="flex items-center justify-between gap-3">
        <div className="text-xs text-slate-600">
          <span className="font-bold text-slate-900">{selected.size}</span> of max 6 selected
        </div>
        <button
          onClick={run}
          disabled={loading || selected.size < 2}
          className="inline-flex items-center gap-2 bg-[#003DA5] hover:bg-[#002F80] disabled:bg-slate-300 text-white rounded-xl px-4 py-2.5 text-sm font-bold transition-colors"
          data-testid="multi-photo-run"
        >
          {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Analysing {selected.size} photos…</> : <><Sparkles className="w-4 h-4" /> Run Multi-angle Analysis</>}
        </button>
      </div>

      {error && (
        <div className="mt-3 p-3 rounded-xl bg-red-50 border border-red-200 text-sm text-red-700" data-testid="multi-photo-error">
          {error}
        </div>
      )}

      {estimate && (
        <div className="mt-4 rounded-xl border border-slate-200 bg-white overflow-hidden fade-in-up" data-testid="multi-photo-result">
          <div className="px-4 py-3 flex items-center justify-between border-b border-slate-100">
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-[#003DA5]" />
              <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">Multi-angle Estimate</span>
              <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-[#E6F0FF] text-[#003DA5]">
                {estimate.photos_analysed} photos combined
              </span>
            </div>
            <button onClick={run} className="text-xs font-semibold text-slate-500 hover:text-[#003DA5] flex items-center gap-1">
              <RefreshCw className="w-3 h-3" /> Re-analyse
            </button>
          </div>
          <div className="p-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="px-3 py-1.5 rounded-full flex items-center gap-1.5" style={{ background: sev.bg, color: sev.fg }}>
                <SevIcon className="w-3.5 h-3.5" />
                <span className="text-xs font-bold uppercase tracking-wider">{estimate.severity}</span>
              </div>
              <div className="text-xs text-slate-500">
                Confidence <span className="font-bold text-slate-700">{Math.round((estimate.confidence ?? 0) * 100)}%</span>
              </div>
            </div>
            <div className="grid sm:grid-cols-3 gap-3 mb-4">
              <div className="p-2.5 rounded-lg border border-slate-100 bg-slate-50">
                <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Repair Type</div>
                <div className="text-sm font-bold text-slate-900 mt-0.5">{estimate.repair_complexity}</div>
              </div>
              <div className="p-2.5 rounded-lg border border-slate-100 bg-slate-50">
                <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Repair Time</div>
                <div className="text-sm font-bold text-slate-900 mt-0.5">{estimate.estimated_days_min}-{estimate.estimated_days_max} days</div>
              </div>
              <div className="p-2.5 rounded-lg border border-slate-100 bg-slate-50">
                <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Parts Damaged</div>
                <div className="text-sm font-bold text-slate-900 mt-0.5">{estimate.damaged_parts?.length || 0} identified</div>
              </div>
            </div>
            {(estimate.estimated_cost_inr_min > 0 || estimate.estimated_cost_inr_max > 0) && (
              <div className="mb-4 p-4 rounded-xl bg-gradient-to-br from-[#003DA5] to-[#0A5BBD] text-white" data-testid="multi-cost-band">
                <div className="text-[10px] font-bold uppercase tracking-[0.2em] opacity-80 mb-1">Indicative repair cost (combined)</div>
                <div className="font-heading font-extrabold text-2xl tracking-tight">
                  ₹{(estimate.estimated_cost_inr_min || 0).toLocaleString("en-IN")} <span className="opacity-60 text-lg">–</span> ₹{(estimate.estimated_cost_inr_max || 0).toLocaleString("en-IN")}
                </div>
                <div className="text-[11px] opacity-80 mt-0.5">
                  Range sharpened across {estimate.photos_analysed} photos. Final amount confirmed by surveyor.
                </div>
              </div>
            )}
            {estimate.damaged_parts?.length > 0 && (
              <div className="mb-3">
                <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500 mb-1.5">Combined damage view</div>
                <div className="flex flex-wrap gap-1.5">
                  {estimate.damaged_parts.map((p, i) => (
                    <span key={i} className="px-2.5 py-1 rounded-full bg-slate-100 text-xs font-semibold text-slate-700">{p}</span>
                  ))}
                </div>
              </div>
            )}
            {estimate.recommendation && (
              <div className="mt-3 p-3 rounded-lg bg-[#F8FAFC] border border-slate-100 text-sm text-slate-700 leading-relaxed">
                <span className="font-bold text-slate-900">Recommendation: </span>{estimate.recommendation}
              </div>
            )}
            {(pincode || insurer) && estimate.is_vehicle !== false && (
              <button
                onClick={() => setShowGarages(true)}
                className="mt-3 w-full inline-flex items-center justify-center gap-2 bg-gradient-to-r from-[#FF7A00] to-[#FF9633] hover:from-[#CC6200] hover:to-[#E07A00] text-white rounded-xl px-4 py-3 text-sm font-bold transition-colors"
                data-testid="multi-garage-handoff"
              >
                <MapPin className="w-4 h-4" /> Find nearby cashless garages
              </button>
            )}
          </div>
        </div>
      )}

      {showGarages && (
        <NearbyGarages
          pincode={pincode}
          insurer={insurer}
          claimId={claimId}
          docIds={Array.from(selected)}
          customerMobile={customerMobile}
          customerName={customerName}
          speciality={estimate?.repair_complexity === "Structural" ? "structural" : estimate?.repair_complexity === "Panel replacement" ? "panel" : "cosmetic"}
          onClose={() => setShowGarages(false)}
        />
      )}
    </div>
  );
}
